import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { accessForPath } from '../config/pageAccess';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const refreshAuth = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me');
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        setPermissions(data.permissions || []);
      }
    } catch (err) {
      // Server not available — treat as not authenticated
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  // Apply a { user, permissions } payload returned by any sign-in path
  // (password login, passkey login, signup verification, invite acceptance).
  const applySession = useCallback((data) => {
    setUser(data.user);
    setPermissions(data.permissions || []);
  }, []);

  const login = async (email, password) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data.error || 'Login failed');
      err.needsVerification = !!data.needsVerification;
      throw err;
    }
    applySession(data);
    return data;
  };

  // Start self-service signup — emails a 6-digit verification code.
  const register = async (email, full_name, salutation_name, password) => {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, full_name, salutation_name, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Sign up failed');
    return data;
  };

  // Complete signup with the emailed code — signs the user in.
  const verifySignup = async (email, code) => {
    const res = await fetch('/api/auth/verify-signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Verification failed');
    applySession(data);
    return data;
  };

  // Accept an admin invitation — signs the user in.
  const acceptInvite = async (token, password, full_name, salutation_name) => {
    const res = await fetch('/api/auth/accept-invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password, full_name, salutation_name }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Could not accept invitation');
    applySession(data);
    return data;
  };

  // Update the signed-in user's full name and salutation name.
  const updateProfile = async (full_name, salutation_name) => {
    const res = await fetch('/api/auth/profile', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ full_name, salutation_name }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Could not update your profile');
    setUser(data.user);
    return data;
  };

  // Delete the signed-in user's own account.
  const deleteAccount = async () => {
    const res = await fetch('/api/auth/account', { method: 'DELETE' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not delete your account');
    setUser(null);
    setPermissions([]);
    return data;
  };

  const logout = async () => {
    await fetch('/api/auth/logout', { method: 'POST' });
    setUser(null);
    setPermissions([]);
  };

  // Does the user hold permission for a private page? Admins always do.
  const hasPermission = useCallback((pagePath) => {
    if (!user) return false;
    if (user.is_admin) return true;
    return permissions.some(p => pagePath.startsWith(p));
  }, [user, permissions]);

  // Full three-state access check (public / member / private).
  const hasAccess = useCallback((pagePath) => {
    const level = accessForPath(pagePath);
    if (level === 'public') return true;
    if (!user) return false;         // member and private both require an account
    if (level === 'member') return true;
    return hasPermission(pagePath);  // private
  }, [user, hasPermission]);

  return (
    <AuthContext.Provider value={{ user, permissions, loading, login, logout, hasPermission, hasAccess, refreshAuth, applySession, register, verifySignup, acceptInvite, updateProfile, deleteAccount }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
